import type { PublicJwk } from '../../clients/index.js'

/**
 * A partner's ES256 key pair, generated in this browser. The private half is
 * never sent anywhere - it exists only in this page's memory until the admin
 * downloads it to hand to the partner. Same shape the onboarding CLI stores
 * under tools/clients/<name>/keys/<kid>.json, so a downloaded key works there too.
 */
export interface PartnerKeyPair {
  kid: string
  publicJwk: PublicJwk
  privateJwk: JsonWebKey
}

const PROOF_LIFETIME_SECONDS = 300
const SIGNING_ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' } as const

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function encodeSegment(value: unknown): string {
  return base64Url(new TextEncoder().encode(JSON.stringify(value)))
}

export async function generatePartnerKey(kid: string): Promise<PartnerKeyPair> {
  const { publicKey, privateKey } = await crypto.subtle.generateKey(SIGNING_ALGORITHM, true, ['sign', 'verify'])
  const publicRaw = await crypto.subtle.exportKey('jwk', publicKey)
  const privateRaw = await crypto.subtle.exportKey('jwk', privateKey)
  if (!publicRaw.x || !publicRaw.y || !privateRaw.d) {
    throw new Error('This browser could not export the generated key pair')
  }

  return {
    kid,
    publicJwk: { kty: 'EC', crv: 'P-256', x: publicRaw.x, y: publicRaw.y, use: 'sig', alg: 'ES256', kid },
    privateJwk: { kty: 'EC', crv: 'P-256', x: privateRaw.x, y: privateRaw.y, d: privateRaw.d },
  }
}

/** Loads a private JWK for signing. Non-extractable: once imported, the key material cannot be read back out of the page. */
export function importSigningKey(privateJwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', privateJwk, SIGNING_ALGORITHM, false, ['sign'])
}

/**
 * Signs an ES256 JWT. WebCrypto's ECDSA signature is already the raw r||s
 * form a JWS ES256 signature uses, so no conversion is needed.
 */
export async function signEs256Jwt(kid: string, claims: Record<string, unknown>, signingKey: CryptoKey): Promise<string> {
  const header = { alg: 'ES256', kid, typ: 'JWT' }
  const signingInput = `${encodeSegment(header)}.${encodeSegment(claims)}`
  const signature = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, signingKey, new TextEncoder().encode(signingInput))

  return `${signingInput}.${base64Url(new Uint8Array(signature))}`
}

/** Signs the proof-of-possession JWT the API expects at POST /admin/m2m/registrations/{id}/proof. */
export async function signProofOfPossession(
  key: PartnerKeyPair,
  registrationId: string,
  challenge: string,
  issuer: string,
): Promise<string> {
  const issuedAt = Math.floor(Date.now() / 1000)
  const claims = {
    registration_transaction: registrationId,
    challenge,
    aud: `${issuer}/admin/m2m/registrations/${registrationId}/proof`,
    iat: issuedAt,
    exp: issuedAt + PROOF_LIFETIME_SECONDS,
    jti: crypto.randomUUID(),
  }

  return signEs256Jwt(key.kid, claims, await importSigningKey(key.privateJwk))
}

/** Saves the key pair as `<kid>.json` - the file the Partner Simulator and the onboarding CLI both read. */
export function downloadPrivateKey(key: PartnerKeyPair): void {
  const blob = new Blob([`${JSON.stringify(key, null, 2)}\n`], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${key.kid}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}
